   // check4dnr/src/components/Signup.js
   import React, { useState, useContext } from 'react';
   import {
     Container,
     TextField,
     Button,
     Typography,
     CircularProgress,
   } from '@mui/material';
   import { signUp, confirmSignUp, resendConfirmationCode } from './cognito';
   import { UserContext } from '../context/UserContext';
   import { useNavigate } from 'react-router-dom';

   function Signup() {
     const { setUserGuid, setFirstName, setLastName } = useContext(UserContext);
     const [formData, setFormData] = useState({
       firstName: '',
       lastName: '',
       email: '',
       password: '',
       confirmPassword: '',
     });
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [signupStatus, setSignupStatus] = useState('');
     const [errors, setErrors] = useState({});
     const [currentStep, setCurrentStep] = useState('signup');
     const [confirmationCode, setConfirmationCode] = useState('');
     const [confirmationError, setConfirmationError] = useState('');
     const navigate = useNavigate();

     const handleInputChange = (e) => {
       setFormData({ ...formData, [e.target.name]: e.target.value });
     };

     const validateForm = () => {
       const newErrors = {};
       if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
       if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
       if (!formData.email) {
         newErrors.email = 'Email is required';
       } else {
         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         if (!emailRegex.test(formData.email)) {
           newErrors.email = 'Invalid email format';
         }
       }
       if (!formData.password) newErrors.password = 'Password is required';
       if (formData.password !== formData.confirmPassword) {
         newErrors.confirmPassword = 'Passwords do not match';
       }
       setErrors(newErrors);
       return Object.keys(newErrors).length === 0;
     };

     const handleSignup = async () => {
       if (!validateForm()) return;
       setIsSubmitting(true);
       setSignupStatus('Signing up...');

       try {
         const response = await signUp(
           formData.email,
           formData.password,
           formData.firstName,
           formData.lastName
         );

         if (response.success) {
           setSignupStatus('Signup successful! A confirmation code has been sent to your email.');
           setCurrentStep('confirm');
         } else {
           setSignupStatus(response.message || 'Signup failed.');
         }
       } catch (error) {
         console.error('Signup error:', error);
         setSignupStatus(error.message || 'An error occurred during signup.');
       } finally {
         setIsSubmitting(false);
       }
     };

     const handleConfirm = async () => {
       if (!confirmationCode.trim()) {
         setConfirmationError('Confirmation code is required.');
         return;
       }
       setIsSubmitting(true);
       setSignupStatus('Confirming your account...');

       try {
         const response = await confirmSignUp(formData.email, confirmationCode);

         if (response.success) {
           setSignupStatus('Email confirmed successfully! You can now log in.');
           // Redirect to login page after a delay
           setTimeout(() => {
             navigate('/login');
           }, 3000);
         } else {
           setSignupStatus(response.message || 'Confirmation failed.');
         }
       } catch (error) {
         console.error('Confirmation error:', error);
         setConfirmationError(error.message || 'An error occurred during confirmation.');
         setSignupStatus('');
       } finally {
         setIsSubmitting(false);
       }
     };

     const handleResendCode = async () => {
       setIsSubmitting(true);
       setSignupStatus('Resending confirmation code...');

       try {
         const response = await resendConfirmationCode(formData.email);

         if (response.success) {
           setSignupStatus('A new confirmation code has been sent to your email.');
         } else {
           setSignupStatus(response.message || 'Resend confirmation code failed.');
         }
       } catch (error) {
         console.error('Resend code error:', error);
         setSignupStatus(error.message || 'An error occurred while resending the confirmation code.');
       } finally {
         setIsSubmitting(false);
       }
     };

     return (
       <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
         {currentStep === 'signup' && (
           <>
             <Typography variant="h4" gutterBottom>
               Sign Up
             </Typography>

             <form noValidate autoComplete="off">
               <TextField
                 fullWidth
                 label="First Name"
                 name="firstName"
                 value={formData.firstName}
                 onChange={handleInputChange}
                 margin="normal"
                 error={!!errors.firstName}
                 helperText={errors.firstName}
               />
               <TextField
                 fullWidth
                 label="Last Name"
                 name="lastName"
                 value={formData.lastName}
                 onChange={handleInputChange}
                 margin="normal"
                 error={!!errors.lastName}
                 helperText={errors.lastName}
               />
               <TextField
                 fullWidth
                 label="Email"
                 name="email"
                 type="email"
                 value={formData.email}
                 onChange={handleInputChange}
                 margin="normal"
                 error={!!errors.email}
                 helperText={errors.email}
               />
               <TextField
                 fullWidth
                 label="Password"
                 name="password"
                 type="password"
                 value={formData.password}
                 onChange={handleInputChange}
                 margin="normal"
                 error={!!errors.password}
                 helperText={errors.password}
               />
               <TextField
                 fullWidth
                 label="Confirm Password"
                 name="confirmPassword"
                 type="password"
                 value={formData.confirmPassword}
                 onChange={handleInputChange}
                 margin="normal"
                 error={!!errors.confirmPassword}
                 helperText={errors.confirmPassword}
               />

               {/* Signup Status */}
               {signupStatus && (
                 <Typography
                   variant="body1"
                   sx={{
                     mt: 3,
                     color: signupStatus.toLowerCase().includes('success') ? 'green' : 'red',
                   }}
                 >
                   {signupStatus}
                 </Typography>
               )}

               {/* Submit Button */}
               <Button
                 variant="contained"
                 color="primary"
                 onClick={handleSignup}
                 fullWidth
                 sx={{ mt: 3 }}
                 disabled={isSubmitting}
               >
                 {isSubmitting ? <CircularProgress size={24} /> : 'Sign Up'}
               </Button>
             </form>
           </>
         )}

         {currentStep === 'confirm' && (
           <>
             <Typography variant="h4" gutterBottom>
               Confirm Your Email
             </Typography>

             <Typography variant="body1" sx={{ mt: 2 }}>
               Please enter the confirmation code sent to <strong>{formData.email}</strong>.
             </Typography>

             <TextField
               fullWidth
               label="Confirmation Code"
               name="confirmationCode"
               value={confirmationCode}
               onChange={(e) => setConfirmationCode(e.target.value)}
               margin="normal"
               error={!!confirmationError}
               helperText={confirmationError}
             />

             {/* Confirmation Status */}
             {signupStatus && (
               <Typography
                 variant="body1"
                 sx={{
                   mt: 3,
                   color: signupStatus.toLowerCase().includes('success') ? 'green' : 'red',
                 }}
               >
                 {signupStatus}
               </Typography>
             )}

             {/* Confirm Button */}
             <Button
               variant="contained"
               color="primary"
               onClick={handleConfirm}
               fullWidth
               sx={{ mt: 3 }}
               disabled={isSubmitting}
             >
               {isSubmitting ? <CircularProgress size={24} /> : 'Confirm Email'}
             </Button>

             {/* Resend Confirmation Code */}
             <Button
               variant="text"
               color="secondary"
               onClick={handleResendCode}
               fullWidth
               sx={{ mt: 2 }}
               disabled={isSubmitting}
             >
               Resend Confirmation Code
             </Button>
           </>
         )}
       </Container>
     );
   }

   export default Signup;